import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import Handler from "../../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../../Helpers/Utils";
import { DetailCard, FieldRow, Skeleton } from "../../../Components/Needed";
import {
    SERVICE_TYPES, SERVICE_ACTIONS, SERVICE_CENTER_TYPES, PAYMENT_MODES,
    formatPreviousValue, serviceStatusVariant, toDateTimeInputValue,
} from "../shared/vehicleFleetConstants";
import Timeline from "../../../Components/Timeline";
import { listBackPath } from "../../../Helpers/useListParams";

const emptyPartRow = () => ({ partName: "", serviceAction: SERVICE_ACTIONS[0], cost: "" });

// A Service record's lifecycle is Initiated -> Started -> Closed, each
// stage unlocking exactly one next action (serviceStart/serviceStartUpdate/
// serviceEnd - see ServiceController.js) - so this page renders only the
// section that stage actually allows, rather than one big form.
const ServiceDetail = () => {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const [service, setService] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const [staffOptions, setStaffOptions] = useState([]);
    const [editing, setEditing] = useState(false);
    const [editServiceName, setEditServiceName] = useState("");
    const [editServiceType, setEditServiceType] = useState("");
    const [editStaffId, setEditStaffId] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);

    const [serviceCenter, setServiceCenter] = useState("");
    const [serviceCenterType, setServiceCenterType] = useState(SERVICE_CENTER_TYPES[0]);
    const [servicedStartAt, setServicedStartAt] = useState("");
    const [savingStart, setSavingStart] = useState(false);

    const [totalCost, setTotalCost] = useState("");
    const [partRows, setPartRows] = useState([emptyPartRow()]);
    const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES[0]);
    const [paymentBy, setPaymentBy] = useState("");
    const [vehicleKm, setVehicleKm] = useState("");
    const [servicedEndAt, setServicedEndAt] = useState("");
    const [savingEnd, setSavingEnd] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [serviceResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/admin/service/${serviceId}` }),
            Handler({ method: "GET", url: `/admin/service/${serviceId}/log`, params: { limit: 20 } }),
        ]);
        const data = serviceResponse?.success ? serviceResponse?.data?.data : null;
        setService(data);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        if (data) {
            setEditServiceName(data.serviceName || "");
            setEditServiceType(data.serviceType || SERVICE_TYPES[0]);
            setEditStaffId(data.staffId || "");
            setServiceCenter(data.serviceCenter || "");
            setServiceCenterType(data.serviceCenterType || SERVICE_CENTER_TYPES[0]);
            setServicedStartAt(toDateTimeInputValue(data.servicedStartAt) || toDateTimeInputValue(new Date()));
            setServicedEndAt(toDateTimeInputValue(new Date()));
        }
        setLoading(false);
    }, [serviceId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "post", url: "/admin/staff/list/0", params: { count: 100 }, data: { employmentStatus: "active" } });
            setStaffOptions(response?.success ? response?.data?.data?.list || [] : []);
        })();
    }, []);

    const onSaveEdit = async () => {
        setSavingEdit(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/service/${serviceId}`,
            data: { serviceName: editServiceName.trim(), serviceType: editServiceType, staffId: editStaffId },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Updated successfully");
            setEditing(false);
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update service"));
        }
        setSavingEdit(false);
    };

    const onStart = async () => {
        if (!serviceCenter.trim() || !servicedStartAt) {
            toast.error("Service center and start date/time are required");
            return;
        }
        setSavingStart(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/service/${serviceId}/start`,
            data: { serviceCenter: serviceCenter.trim(), serviceCenterType, servicedStartAt: new Date(servicedStartAt).toISOString() },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Service started");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to start service"));
        }
        setSavingStart(false);
    };

    const onUpdateStart = async () => {
        setSavingStart(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/service/${serviceId}/start/update`,
            data: { serviceCenter: serviceCenter.trim(), serviceCenterType, servicedStartAt: new Date(servicedStartAt).toISOString() },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Updated successfully");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update start details"));
        }
        setSavingStart(false);
    };

    const updatePartRow = (index, field, value) => setPartRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    const addPartRow = () => setPartRows((prev) => [...prev, emptyPartRow()]);
    const removePartRow = (index) => setPartRows((prev) => prev.filter((_, i) => i !== index));

    const onEnd = async () => {
        const cleaned = partRows.filter((r) => r.partName.trim() || String(r.cost).trim());
        if (String(totalCost).trim() === "" || Number(totalCost) < 0) {
            toast.error("A non-negative total cost is required");
            return;
        }
        if (!cleaned.length) {
            toast.error("Add at least 1 part/service line");
            return;
        }
        const invalid = cleaned.some((r) => !r.partName.trim() || String(r.cost).trim() === "" || Number(r.cost) < 0);
        if (invalid) {
            toast.error("Each line needs a part name and a non-negative cost");
            return;
        }
        if (!paymentBy.trim()) {
            toast.error("Paid by is required");
            return;
        }
        if (String(vehicleKm).trim() === "" || Number(vehicleKm) < 0) {
            toast.error("A non-negative vehicle odometer reading is required");
            return;
        }
        if (!servicedEndAt) {
            toast.error("Service end date/time is required");
            return;
        }
        setSavingEnd(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/service/${serviceId}/end`,
            data: {
                totalCost: Number(totalCost),
                serviceDetails: cleaned.map((r) => ({ partName: r.partName.trim(), serviceAction: r.serviceAction, cost: Number(r.cost) })),
                paymentMode,
                paymentBy: paymentBy.trim(),
                vehicleKm: Number(vehicleKm),
                servicedEndAt: new Date(servicedEndAt).toISOString(),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Service closed successfully");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to close service"));
        }
        setSavingEnd(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={120} />
            </div>
        );
    }

    if (!service) {
        return (
            <div className="settings-card text-center py-5">
                <p className="text-muted mb-3">Service record not found.</p>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(listBackPath("/vehicle-fleet/services"))}>Back to services</button>
            </div>
        );
    }

    const vehiclePath = service.vehicleType && service.vehicleId ? `/vehicle-fleet/${service.vehicleType}/${service.vehicleId}` : null;

    return (
        <>
            <button type="button" className="btn btn-link text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/vehicle-fleet/services"))}>
                <FaArrowLeft /> Back to services
            </button>

            <div className="row g-4">
                <div className="col-12 col-lg-7">
                    <DetailCard title={service.serviceName}>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                            <span className="badge rounded-pill text-bg-light border">{service.serviceType}</span>
                            <span className={`badge rounded-pill ${serviceStatusVariant(service.serviceStatus)}`}>{service.serviceStatus}</span>
                            {service.vehicle ? (
                                vehiclePath ? (
                                    <button type="button" className="btn btn-sm btn-link p-0" onClick={() => navigate(vehiclePath)}>
                                        {service.vehicle.registrationNo} ({service.vehicle.brand} {service.vehicle.modelName})
                                    </button>
                                ) : (
                                    <span className="small text-muted">{service.vehicle.registrationNo}</span>
                                )
                            ) : null}
                        </div>

                        <FieldRow label="Assigned staff" value={service.staffName} />
                        <FieldRow label="Created" value={formatDateTime(service.createdAt)} />

                        {service.serviceStatus === "Initiated" ? (
                            <div className="mt-3 pt-3 border-top">
                                {editing ? (
                                    <div className="row g-2 mb-3">
                                        <div className="col-12">
                                            <label className="form-label">Service name</label>
                                            <input type="text" className="form-control" value={editServiceName} onChange={(e) => setEditServiceName(e.target.value)} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Service type</label>
                                            <select className="form-select" value={editServiceType} onChange={(e) => setEditServiceType(e.target.value)}>
                                                {SERVICE_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Assigned staff</label>
                                            <select className="form-select" value={editStaffId} onChange={(e) => setEditStaffId(e.target.value)}>
                                                {staffOptions.map((s) => (<option key={s.staffId} value={s.staffId}>{s.name} - {s.designation}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-12 d-flex gap-2 justify-content-end">
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(false)}>Cancel</button>
                                            <button type="button" className="btn btn-sm btn-primary" onClick={onSaveEdit} disabled={savingEdit}>{savingEdit ? "Saving..." : "Save"}</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setEditing(true)}>Edit service</button>
                                )}

                                <div className="fw-semibold small mb-2">Start service</div>
                                <div className="row g-2">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label">Service center</label>
                                        <input type="text" className="form-control" value={serviceCenter} onChange={(e) => setServiceCenter(e.target.value)} placeholder="e.g. Downtown Motors" />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label">Service center type</label>
                                        <select className="form-select" value={serviceCenterType} onChange={(e) => setServiceCenterType(e.target.value)}>
                                            {SERVICE_CENTER_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label">Started at</label>
                                        <input type="datetime-local" className="form-control" value={servicedStartAt} onChange={(e) => setServicedStartAt(e.target.value)} />
                                    </div>
                                    <div className="col-12 d-flex justify-content-end">
                                        <button type="button" className="btn btn-sm btn-primary" onClick={onStart} disabled={savingStart}>{savingStart ? "Saving..." : "Start service"}</button>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {service.serviceStatus === "Started" ? (
                            <>
                                <div className="mt-3 pt-3 border-top">
                                    <div className="fw-semibold small mb-2">Start details</div>
                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Service center</label>
                                            <input type="text" className="form-control" value={serviceCenter} onChange={(e) => setServiceCenter(e.target.value)} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Service center type</label>
                                            <select className="form-select" value={serviceCenterType} onChange={(e) => setServiceCenterType(e.target.value)}>
                                                {SERVICE_CENTER_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Started at</label>
                                            <input type="datetime-local" className="form-control" value={servicedStartAt} onChange={(e) => setServicedStartAt(e.target.value)} />
                                        </div>
                                        <div className="col-12 d-flex justify-content-end">
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onUpdateStart} disabled={savingStart}>{savingStart ? "Saving..." : "Update start details"}</button>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3 pt-3 border-top">
                                    <div className="fw-semibold small mb-2">End service</div>
                                    <div className="row g-2 mb-2">
                                        {partRows.map((row, index) => (
                                            <div key={index} className="col-12">
                                                <div className="row g-2 align-items-center">
                                                    <div className="col-12 col-md-5">
                                                        <input type="text" className="form-control" placeholder="Part / service name" value={row.partName} onChange={(e) => updatePartRow(index, "partName", e.target.value)} />
                                                    </div>
                                                    <div className="col-6 col-md-3">
                                                        <select className="form-select" value={row.serviceAction} onChange={(e) => updatePartRow(index, "serviceAction", e.target.value)}>
                                                            {SERVICE_ACTIONS.map((a) => (<option key={a} value={a}>{a}</option>))}
                                                        </select>
                                                    </div>
                                                    <div className="col-5 col-md-3">
                                                        <input type="number" className="form-control" placeholder="Cost" min="0" value={row.cost} onChange={(e) => updatePartRow(index, "cost", e.target.value)} />
                                                    </div>
                                                    <div className="col-1">
                                                        {partRows.length > 1 ? (
                                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removePartRow(index)}>&times;</button>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="col-12">
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addPartRow}>+ Add line</button>
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <label className="form-label">Total cost</label>
                                            <input type="number" className="form-control" min="0" value={totalCost} onChange={(e) => setTotalCost(e.target.value)} />
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <label className="form-label">Payment mode</label>
                                            <select className="form-select" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                                                {PAYMENT_MODES.map((p) => (<option key={p} value={p}>{p}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <label className="form-label">Paid by</label>
                                            <input type="text" className="form-control" value={paymentBy} onChange={(e) => setPaymentBy(e.target.value)} placeholder="Name / reference" />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Odometer reading</label>
                                            <input type="number" className="form-control" min="0" value={vehicleKm} onChange={(e) => setVehicleKm(e.target.value)} />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Ended at</label>
                                            <input type="datetime-local" className="form-control" value={servicedEndAt} onChange={(e) => setServicedEndAt(e.target.value)} />
                                        </div>
                                        <div className="col-12 d-flex justify-content-end">
                                            <button type="button" className="btn btn-sm btn-primary" onClick={onEnd} disabled={savingEnd}>{savingEnd ? "Saving..." : "Close service"}</button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : null}

                        {service.serviceStatus === "Closed" ? (
                            <div className="mt-3 pt-3 border-top">
                                <FieldRow label="Service center" value={`${service.serviceCenter || "-"} (${service.serviceCenterType || "-"})`} />
                                <FieldRow label="Started at" value={formatDateTime(service.servicedStartAt)} />
                                <FieldRow label="Ended at" value={formatDateTime(service.servicedEndAt)} />
                                <FieldRow label="Odometer reading" value={service.vehicleKm !== undefined ? `${service.vehicleKm} km` : "-"} />
                                <FieldRow label="Total cost" value={service.totalCost !== undefined ? `₹${service.totalCost}` : "-"} />
                                <FieldRow label="Payment" value={`${service.paymentMode || "-"} - ${service.paymentBy || "-"}`} />
                                {service.serviceDetails?.length ? (
                                    <div className="mt-2">
                                        <div className="small fw-semibold mb-1">Parts / services</div>
                                        {service.serviceDetails.map((d, i) => (
                                            <div key={i} className="small d-flex justify-content-between border-bottom py-1">
                                                <span>{d.partName} <span className="text-muted">({d.serviceAction})</span></span>
                                                <span>₹{d.cost}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </DetailCard>
                </div>

                <div className="col-12 col-lg-5">
                    {/* <DetailCard title="Activity Log">
                        {logs.length ? (
                            <ul className="hub-log-list">
                                {logs.map((log) => (
                                    <li key={log._id || `${log.createdAt}-${log.action}`} className="hub-log-item">
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className={`badge rounded-pill ${log.action === "Write" ? "text-bg-primary" : "text-bg-secondary"}`}>{log.action}</span>
                                            <span className="text-muted small">{formatDateTime(log.createdAt)}</span>
                                        </div>
                                        <div className="small mt-1">{log?.previousData?.msg || "-"}</div>
                                        {log?.previousData?.data && Object.keys(log.previousData.data).length ? (
                                            <div className="hub-log-diff">
                                                {Object.entries(log.previousData.data).map(([field, value]) => (
                                                    <div key={field}>
                                                        <span className="text-muted">{field}:</span> was {formatPreviousValue(value)}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : ""}
                                        <div className="text-muted small mt-1">By {log.userId}</div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-muted mb-0">No activity recorded yet.</p>
                        )}
                    </DetailCard> */}
                    <Timeline userLog={logs}/>
                </div>
            </div>
        </>
    );
};

export default ServiceDetail;
